import {
  Alert,
  CloseButton,
  Input,
  NativeSelect,
  TextInput,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { Plus, Type } from "lucide-react";
import { useState, type FormEvent } from "react";

import { isApiError } from "../../api/errors";
import type { BrandExtract, BrandIn as BrandBody } from "../../api/generated/model";
import {
  brandInAudienceOneMax,
  brandInColorsMax,
  brandInDescriptionMax,
  brandInFontsMax,
  brandInGuidelinesOneMax,
  brandInNameMax,
  brandInToneOfVoiceOneMax,
  brandInWebsiteOneMax,
  BrandExtract as BrandExtractAnswer,
  fontNameMax,
} from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { pictureOf, type PickerLibrary } from "../../platform/media/ImagePicker";
import { importPicture } from "../../platform/media/url-import";
import { toUserMessage } from "../../platform/messages";
import { FieldLabel, infoLabel } from "../../ui/FieldLabel";
import { FormDivider } from "../../ui/FormDivider";
import { ColorsInput } from "./ColorsInput";
import { brandIn, fieldIssues, type BrandDraft, type FontDraft } from "./drafts";
import classes from "./forms.module.css";
import { ImageField } from "./ImageField";
import { ImportRow } from "./ImportRow";
import { SaveButton } from "./SaveButton";
import { useExtract } from "./use-extract";

/** Live's one sentence for any failed extraction (its toast; ours a line, web/AGENTS.md). */
export const BRAND_EXTRACT_FAILED = "Failed to extract brand";
/** Said when the logo the page names did not import (#1469), never left out silently. */
export const LOGO_LEFT_OUT = "The page's logo could not be imported. Upload it by hand.";

// What to fix, per request field, when the generated schema refuses it.
const MESSAGES: Record<string, string> = {
  name: `Give the brand a name of up to ${brandInNameMax} characters.`,
  description: `Describe the brand in up to ${brandInDescriptionMax} characters.`,
  website: "Enter a web address that starts with http:// or https://, or leave it empty.",
  logo_asset_id: "This image cannot be the logo; choose another.",
  colors: "Each color needs a hex code such as #1A2B3C.", // raw-color: an example in a hint
  fonts: "Each font needs a name.",
};

export interface BrandFormProps {
  initial: BrandDraft;
  /** A saved brand: the divider says "saved brand data" and the button "Save". */
  editing: boolean;
  pending: boolean;
  /** The last write's failure: a taken name shows on the name field, the rest by the button. */
  error: unknown;
  onSubmit: (body: BrandBody) => void;
  /**
   * The library the logo uploads into and is chosen from: this brand's, or on a new brand the
   * team's first; `null` when the team has no brand yet, `undefined` while that is not known.
   */
  library: PickerLibrary | null | undefined;
}

/** The whole brand, as `POST` and `PUT` take it (A3), in the live layout. */
export function BrandForm(props: BrandFormProps) {
  const { initial, editing, pending, error, onSubmit, library } = props;
  const [draft, setDraft] = useState(initial);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [logoLeftOut, setLogoLeftOut] = useState(false);
  const set = <K extends keyof BrandDraft>(key: K, value: BrandDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const text = (key: keyof BrandDraft) => (event: { currentTarget: { value: string } }) =>
    set(key, event.currentTarget.value);

  // Live's required fields: the name, the logo and the description (live's schema).
  const ready =
    draft.name.trim() !== "" &&
    draft.description.trim() !== "" &&
    draft.logoAssetId !== "" &&
    !uploading;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    const parsed = brandIn(draft);
    if (!parsed.success) {
      const found: Record<string, string> = {};
      for (const field of fieldIssues(parsed.error).keys()) {
        found[field] = MESSAGES[field] ?? "This value is not valid.";
      }
      setProblems(found);
      return;
    }
    setProblems({});
    onSubmit(parsed.data);
  };
  const nameTaken = isApiError(error, "BRAND_NAME_TAKEN");

  // Live's "Extract" (#249): the page read in a job of the library's brand, then the form filled
  // with live's fields; the logo is imported into that library, as the form can show only the
  // app's own pictures, and the library shows it only once the save names it (G15, 9).
  const extract = useExtract({
    teamId: library?.teamId ?? "",
    brandId: library?.brandId ?? null,
    answer: BrandExtractAnswer,
    onAnswer: async (found: BrandExtract) => {
      let logo: { assetId: string; url: string | null } | null = null;
      if (found.logo !== null && library) {
        try {
          const asset = await importPicture(library.teamId, library.brandId, found.logo);
          logo = { assetId: asset.id, url: pictureOf(asset) };
        } catch {
          logo = null;
        }
      }
      setLogoLeftOut(found.logo !== null && logo === null);
      setDraft((current) => ({
        ...current,
        name: found.name.slice(0, brandInNameMax),
        description: found.description.slice(0, brandInDescriptionMax),
        website: extractedUrl(current.website),
        ...(logo === null ? {} : { logoAssetId: logo.assetId, logoUrl: logo.url }),
        colors: found.colors
          .slice(0, brandInColorsMax)
          .map((c) => ({ hex: c.hex, label: c.label })),
        fonts: found.fonts.slice(0, 1).map((name) => ({ name, source: "google" as const })),
      }));
    },
  });

  return (
    <form className={classes.form} onSubmit={submit} noValidate>
      {/* Live imports from the website; ours keeps it as the brand's website (A3). */}
      <ImportRow
        label="Import from Website"
        description="Auto-extract logo, colors & description"
        field={{
          value: draft.website,
          onChange: (website) => set("website", website),
          maxLength: brandInWebsiteOneMax,
          error: problems["website"],
        }}
        extract={{
          running: extract.running,
          onExtract: () => {
            setLogoLeftOut(false);
            extract.start({ type: "extract.brand", url: draft.website.trim() });
          },
          unavailable: library
            ? undefined
            : "Extraction runs in a brand's library: add it once this brand exists.",
        }}
      />
      {extract.failed ? (
        <Alert color="red" variant="light" className={classes.alert}>
          {BRAND_EXTRACT_FAILED}
        </Alert>
      ) : null}
      {logoLeftOut && !extract.running ? (
        <Alert color="yellow" variant="light" className={classes.alert} data-logo-left-out>
          {LOGO_LEFT_OUT}
        </Alert>
      ) : null}
      <FormDivider
        label={
          extract.running
            ? "extracting brand info"
            : editing
              ? "saved brand data"
              : "or enter manually"
        }
      />
      <fieldset
        className={classes.extractable}
        disabled={extract.running}
        data-extracting={extract.running || undefined}
      >
        <div className={classes.fields}>
          <TextInput
            className={classes.half}
            {...infoLabel({
              label: "Brand Name",
              required: true,
              info: "Used across all AI-generated creatives to consistently identify your brand in every ad, image, and video.",
            })}
            required
            withAsterisk={false}
            placeholder="Enter brand name"
            maxLength={brandInNameMax}
            value={draft.name}
            onChange={text("name")}
            error={nameTaken ? toUserMessage(error) : problems["name"]}
          />
          <div className={classes.row}>
            <Input.Wrapper
              label={
                <FieldLabel
                  label="Brand Logo"
                  required
                  info="Optionally embedded into ad creatives and video content — only applied if you choose to include it during the creative step."
                  spoken
                />
              }
              description="Add brand logo"
              labelElement="div"
            >
              <ImageField
                name="logo"
                label="Logo"
                shape="logo"
                value={
                  draft.logoAssetId === ""
                    ? null
                    : { assetId: draft.logoAssetId, url: draft.logoUrl }
                }
                library={library ?? undefined}
                {...(library === null
                  ? {
                      unavailable:
                        "A logo goes into a brand's library: add it once this brand exists.",
                    }
                  : {})}
                onChange={(picked) =>
                  setDraft((current) => ({
                    ...current,
                    logoAssetId: picked?.assetId ?? "",
                    logoUrl: picked?.url ?? null,
                  }))
                }
                onBusy={setUploading}
                error={problems["logo_asset_id"]}
              />
            </Input.Wrapper>
            <ColorsInput
              label="Brand Colors"
              description={`Add up to ${brandInColorsMax} brand colors`}
              info="Saved to your brand profile and applied to creatives only when selected during the creative step."
              value={draft.colors}
              onChange={(colors) => set("colors", colors)}
              max={brandInColorsMax}
              error={problems["colors"]}
            />
          </div>
          <div className={classes.row}>
            <FontsInput
              value={draft.fonts}
              onChange={(fonts) => set("fonts", fonts)}
              error={problems["fonts"]}
            />
            <Textarea
              {...infoLabel({
                label: "Brand Description",
                required: true,
                info: "Feeds the AI with context about your brand to generate more relevant, accurate, and on-brand content across all formats.",
                description:
                  "Describe your brand, what do you do, your values, and target audience",
              })}
              required
              withAsterisk={false}
              placeholder="Describe your brand"
              rows={9}
              maxLength={brandInDescriptionMax}
              value={draft.description}
              onChange={text("description")}
              error={problems["description"]}
            />
          </div>
          {/* Ours alone (D12): live's form has no place for these, and a PUT must keep them. */}
          <div className={classes.row}>
            <Textarea
              label="Tone of voice"
              rows={3}
              maxLength={brandInToneOfVoiceOneMax}
              value={draft.toneOfVoice}
              onChange={text("toneOfVoice")}
            />
            <Textarea
              label="Audience"
              rows={3}
              maxLength={brandInAudienceOneMax}
              value={draft.audience}
              onChange={text("audience")}
            />
          </div>
          <Textarea
            label="Guidelines"
            rows={4}
            maxLength={brandInGuidelinesOneMax}
            value={draft.guidelines}
            onChange={text("guidelines")}
          />
        </div>
      </fieldset>
      {error !== null && error !== undefined && !nameTaken ? (
        <div className={classes.alert}>
          <ErrorAlert error={error} />
        </div>
      ) : null}
      <div className={classes.footer}>
        <SaveButton
          addLabel="Add Brand"
          editing={editing}
          ready={ready}
          required="Complete all required fields to create your brand. Required: Brand Name, Logo, and Description"
          pending={pending}
        />
      </div>
    </form>
  );
}

const SOURCES = [
  { value: "google", label: "Google font" },
  { value: "custom", label: "Custom font" },
];

interface FontsInputProps {
  value: FontDraft[];
  onChange: (fonts: FontDraft[]) => void;
  error?: string | undefined;
}

/**
 * Fonts under live's "Brand Fonts": each a card with a tinted header (its name, source and a
 * remove button) over a specimen in that font, and a dashed "+" zone to add one. Live uploads
 * a font file; the API keeps up to five fonts by name and source (A3), with no file (audit D13).
 */
function FontsInput({ value, onChange, error }: FontsInputProps) {
  const update = (index: number, patch: Partial<FontDraft>) =>
    onChange(value.map((font, at) => (at === index ? { ...font, ...patch } : font)));
  return (
    <Input.Wrapper
      label={
        <FieldLabel
          label="Brand Fonts"
          info="Saved to your brand profile and applied to text overlays only when selected during the creative step."
          spoken
        />
      }
      description={`Up to ${brandInFontsMax} fonts, by name`}
      error={error}
      labelElement="div"
    >
      <div className={classes.list}>
        {value.map((font, index) => (
          // Rows have no id of their own; each is the font at that position.
          <div key={index} className={classes.font}>
            <div className={classes.fontHeader}>
              <Type size={14} aria-hidden />
              <TextInput
                variant="unstyled"
                size="xs"
                className={classes.grow}
                aria-label={`Font ${index + 1}`}
                placeholder="Font name"
                maxLength={fontNameMax}
                value={font.name}
                onChange={(event) => update(index, { name: event.currentTarget.value })}
              />
              <NativeSelect
                variant="unstyled"
                size="xs"
                aria-label={`Source ${index + 1}`}
                data={SOURCES}
                value={font.source}
                onChange={(event) =>
                  update(index, {
                    source: event.currentTarget.value === "custom" ? "custom" : "google",
                  })
                }
              />
              <CloseButton
                size="sm"
                aria-label={`Remove font ${index + 1}`}
                onClick={() => onChange(value.filter((_, at) => at !== index))}
              />
            </div>
            {/* The font's own name is data: the specimen shows in it where the browser has it. */}
            <div className={classes.specimen} style={{ fontFamily: `"${font.name}", inherit` }}>
              ABCDEFGHIJKLMNOPQRS
              <br />
              abcdefghijklmnopqrstuvwxyz
              <br />
              1234567890 !@#$%
            </div>
          </div>
        ))}
        {value.length < brandInFontsMax ? (
          <UnstyledButton
            className={`${classes.zoneAdd} ${classes.fontAdd}`}
            aria-label="Add a font"
            onClick={() => onChange([...value, { name: "", source: "google" }])}
          >
            <Plus size={20} strokeWidth={1.5} aria-hidden />
          </UnstyledButton>
        ) : null}
      </div>
    </Input.Wrapper>
  );
}

/** The website as the extraction read it: `https://` where none was typed, as live adds one. */
function extractedUrl(typed: string): string {
  const url = typed.trim();
  if (/^https:\/\//i.test(url)) return url;
  return `https://${url.replace(/^http:\/\//i, "")}`;
}
